"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { createProjectionStore, type Projection } from "../src/ui-projection-store";
import { useRunyiValueSync } from "../lib/runyi-value-sync";

export const RUNYI_UI_COORDINATOR_CONTRACT = "RUNYI_UNIVERSAL_UI_COORDINATOR_V1";
export const RUNYI_UI_PROJECTION_CONTRACT = "RUNYI_UNIVERSAL_UI_PROJECTION_CLIENT_V2";
export const RUNYI_UI_PROJECTION_EVENT = "runyi:ui-projection";
export const RUNYI_UI_PROJECTION_INVALIDATE_EVENT = "runyi:ui-projection:invalidate";
export type RunyiUiProjection = Projection & {
  ok?: boolean; screen?: string; generated_at?: string; expires_at?: string;
};
type ProjectionStore = ReturnType<typeof createProjectionStore<RunyiUiProjection>>;
const stores = new WeakMap<SupabaseClient, ProjectionStore>();
function storeFor(client: SupabaseClient) {
  let value = stores.get(client);
  if (!value) { value = createProjectionStore<RunyiUiProjection>(); stores.set(client, value); }
  return value;
}
function normalizedProjectionScreen(key: string) { return String(key ?? "").trim().toUpperCase(); }
function clearLegacyProjectionStorage() {
  try {
    // Remove the former screen-only persistent cache; never restore it for a user.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith("runyi:ui-projection:last-known-good:")) localStorage.removeItem(key);
    }
  } catch {}
}
export function useRunyiUiProjection({ supabase, connected, screenKey }: {
  supabase: SupabaseClient | null; connected: boolean; screenKey: string;
}) {
  const key = normalizedProjectionScreen(screenKey);
  const [projection, setProjection] = useState<RunyiUiProjection | null>(null);
  const refreshProjection = useRef<() => Promise<boolean | void>>(async () => false);
  useRunyiValueSync({ supabase, connected, domains: ["all"],
    onRefresh: () => refreshProjection.current(), intervalMs: 15_000 });
  useEffect(() => {
    let cancelled = false, retryTimer = 0;
    setProjection(null);
    clearLegacyProjectionStorage();
    if (!connected || !supabase || !key) return;
    const store = storeFor(supabase);
    let sessionGeneration = 0;
    const load = async (attempt = 0) => {
      if (cancelled || document.visibilityState !== "visible" || navigator.onLine === false) return;
      const generation = sessionGeneration;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (cancelled || generation !== sessionGeneration) return;
        const actor = sessionData.session?.user?.id ?? null;
        store.identify(actor);
        if (!actor) { setProjection(null); return; }
        const next = await store.load(key, async () => {
          const { data, error } = await supabase.rpc("runyi_service_ui_screen_read", {
            p_screen_key: key, p_actor_user_id: actor,
          });
          if (error) throw error;
          // Recheck identity after the asynchronous RPC, not only before it.
          const { data: current } = await supabase.auth.getSession();
          store.identify(current.session?.user?.id ?? null);
          return (data ?? null) as RunyiUiProjection | null;
        });
        if (cancelled || generation !== sessionGeneration || store.getActor() !== actor || !next) return;
        setProjection(next);
        window.dispatchEvent(new CustomEvent(RUNYI_UI_PROJECTION_EVENT, {
          detail: { screenKey: key, actorId: actor, clientScope: store, projection: next },
        }));
        return next.stale !== true;
      } catch (error) {
        if (cancelled || generation !== sessionGeneration) return;
        const previous = store.get(key);
        setProjection(previous ? { ...previous, stale: true, background_updating: true,
          last_known_good: true, last_error: error instanceof Error ? error.message : "Refresh delayed" }
          : { screen: key, status: "UPDATING", stale: true, background_updating: true,
            last_known_good: false, last_error: "Dashboard update delayed" });
        const delay = [500, 1500, 3000][attempt];
        if (delay !== undefined) {
          clearTimeout(retryTimer);
          retryTimer = window.setTimeout(() => void load(attempt + 1), delay);
        }
        return false;
      }
    };
    const { data: auth } = supabase.auth.onAuthStateChange((_event, session) => {
      sessionGeneration++;
      store.identify(session?.user?.id ?? null);
      setProjection(null);
      // Do not await another auth call inside the auth callback.
      clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => void load(), 0);
    });
    const projectionEvent = (event: Event) => {
      const d = (event as CustomEvent).detail;
      if (!cancelled && d?.screenKey === key && d?.actorId === store.getActor()
          && d?.clientScope === store && d?.projection) setProjection(d.projection);
    };
    const invalidate = (event: Event) => {
      const target = normalizedProjectionScreen((event as CustomEvent).detail?.screenKey ?? "");
      if (!target || target === key) void load();
    };
    const wake = () => { void load(); };
    window.addEventListener(RUNYI_UI_PROJECTION_EVENT, projectionEvent);
    window.addEventListener(RUNYI_UI_PROJECTION_INVALIDATE_EVENT, invalidate);
    window.addEventListener("online", wake);
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);
    // Reuse RUNYI's shared realtime/reconciliation hook; no parallel poller.
    refreshProjection.current = () => load();
    void load();
    return () => {
      cancelled = true; sessionGeneration++;
      clearTimeout(retryTimer); auth.subscription.unsubscribe();
      refreshProjection.current = async () => false;
      window.removeEventListener(RUNYI_UI_PROJECTION_EVENT, projectionEvent);
      window.removeEventListener(RUNYI_UI_PROJECTION_INVALIDATE_EVENT, invalidate);
      window.removeEventListener("online", wake); window.removeEventListener("focus", wake);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [supabase, connected, key]);
  return projection;
}

export type RunyiUiShellMode = "DOCUMENT" | "WORKSPACE" | "CHAT" | "LIST" | "OVERLAY";

export type RunyiUiLayoutContract = {
  mode: RunyiUiShellMode;
  scrollOwner: "DOCUMENT" | "CONTENT";
  keyboardAware: boolean;
  source: "DECLARED" | "RESOLVED";
};

function normalizeMode(value: unknown): RunyiUiShellMode | null {
  const mode = String(value ?? "").trim().toUpperCase();
  if (mode === "DOCUMENT" || mode === "WORKSPACE" || mode === "CHAT" || mode === "LIST" || mode === "OVERLAY") {
    return mode;
  }
  return null;
}

export function resolveRunyiUiLayoutContract({
  payload,
  rendererKey,
  architectureKey,
  screenKey,
}: {
  payload: Record<string, any> | null;
  rendererKey?: string | null;
  architectureKey?: string | null;
  screenKey?: string | null;
}): RunyiUiLayoutContract {
  const rendererContract = (payload?.renderer_contract ?? {}) as Record<string, any>;
  const registeredLayout = (rendererContract.layout_contract ?? {}) as Record<string, any>;
  const uiLayout = (payload?.ui_layout_contract ?? {}) as Record<string, any>;
  const renderContract = (payload?.render_contract ?? {}) as Record<string, any>;
  const declared = normalizeMode(
    uiLayout.mode
      ?? uiLayout.shell_mode
      ?? registeredLayout.shell_mode
      ?? renderContract.layout_mode,
  );

  const signature = [
    rendererKey,
    architectureKey,
    screenKey,
    rendererContract.workspace_type,
    registeredLayout.body,
    payload?.bot_workspace_ui_contract?.workspace_type,
  ].filter(Boolean).join(" ").toUpperCase();

  const mode = declared
    ?? (/(WORKSPACE|CONVERSATION|CHAT|BOT)/.test(signature) ? "WORKSPACE" : "DOCUMENT");

  return {
    mode,
    scrollOwner: mode === "DOCUMENT" || mode === "LIST" ? "DOCUMENT" : "CONTENT",
    keyboardAware: mode === "WORKSPACE" || mode === "CHAT",
    source: declared ? "DECLARED" : "RESOLVED",
  };
}

function visibleElementHeight(selector: string) {
  const element = document.querySelector<HTMLElement>(selector);
  if (!element) return 0;
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return 0;
  return Math.max(0, element.getBoundingClientRect().height);
}

function applyRunyiUiMetrics() {
  const root = document.documentElement;
  const viewport = window.visualViewport;
  const viewportHeight = viewport?.height ?? window.innerHeight;
  const headerHeight = visibleElementHeight('[data-runyi-ui-chrome="header"]');
  const bottomNavHeight = visibleElementHeight('[data-runyi-ui-chrome="bottom-nav"]');
  const keyboardInset = viewport
    ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
    : 0;
  const availableHeight = Math.max(0, viewportHeight - headerHeight - bottomNavHeight);

  root.style.setProperty("--runyi-ui-viewport-height", String(viewportHeight) + "px");
  root.style.setProperty("--runyi-ui-header-height", String(headerHeight) + "px");
  root.style.setProperty("--runyi-ui-bottom-nav-height", String(bottomNavHeight) + "px");
  root.style.setProperty("--runyi-ui-keyboard-inset", String(keyboardInset) + "px");
  root.style.setProperty("--runyi-ui-available-height", String(availableHeight) + "px");

  window.dispatchEvent(new CustomEvent("runyi:ui-coordinator:metrics", {
    detail: {
      contract: RUNYI_UI_COORDINATOR_CONTRACT,
      viewportHeight,
      headerHeight,
      bottomNavHeight,
      keyboardInset,
      availableHeight,
    },
  }));
}

export function RunyiUiRuntimeCoordinator({
  supabase = null,
  connected = false,
  screenKey = "ROOT",
}: {
  supabase?: SupabaseClient | null;
  connected?: boolean;
  screenKey?: string;
} = {}) {
  const uiProjection = useRunyiUiProjection({ supabase, connected, screenKey });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const state = String(
      uiProjection?.payload?.ui_projection?.state
      ?? uiProjection?.projection_state
      ?? uiProjection?.status
      ?? "UNKNOWN",
    ).toUpperCase();
    document.documentElement.dataset.runyiUiProjectionScreen = normalizedProjectionScreen(screenKey);
    document.documentElement.dataset.runyiUiProjectionState = state;
  }, [uiProjection, screenKey]);

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    const observeChrome = () => {
      resizeObserver?.disconnect();
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => applyRunyiUiMetrics());
        const header = document.querySelector<HTMLElement>('[data-runyi-ui-chrome="header"]');
        const bottomNav = document.querySelector<HTMLElement>('[data-runyi-ui-chrome="bottom-nav"]');
        if (header) resizeObserver.observe(header);
        if (bottomNav) resizeObserver.observe(bottomNav);
      }
      applyRunyiUiMetrics();
    };

    observeChrome();

    const visualViewport = window.visualViewport;
    window.addEventListener("resize", applyRunyiUiMetrics);
    window.addEventListener("orientationchange", applyRunyiUiMetrics);
    visualViewport?.addEventListener("resize", applyRunyiUiMetrics);
    visualViewport?.addEventListener("scroll", applyRunyiUiMetrics);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", applyRunyiUiMetrics);
      window.removeEventListener("orientationchange", applyRunyiUiMetrics);
      visualViewport?.removeEventListener("resize", applyRunyiUiMetrics);
      visualViewport?.removeEventListener("scroll", applyRunyiUiMetrics);
    };
  }, []);

  useEffect(() => {
    if (!connected || !supabase) return;

    const reported = new Set<string>();
    let validationTimer = 0;

    const record = async (issueKey: string, elementKey: string, observed: Record<string, unknown>) => {
      const key = issueKey + ":" + elementKey;
      if (reported.has(key)) return;
      reported.add(key);
      await supabase.rpc("runyi_service_ui_client_diagnostic_record", {
        p_screen_key: screenKey,
        p_issue_key: issueKey,
        p_severity: "WARNING",
        p_element_key: elementKey,
        p_observed: observed,
      });
    };

    const resolve = async (issueKey: string, elementKey: string) => {
      const key = issueKey + ":" + elementKey;
      if (!reported.has(key)) return;
      reported.delete(key);
      await supabase.rpc("runyi_service_ui_client_diagnostic_resolve", {
        p_screen_key: screenKey,
        p_issue_key: issueKey,
        p_element_key: elementKey,
      });
    };

    const validate = () => {
      if (uiProjection) {
        const projectionState = String(
          uiProjection.payload?.ui_projection?.state
          ?? uiProjection.projection_state
          ?? uiProjection.status
          ?? "UNKNOWN",
        ).toUpperCase();
        const projectionComplete = uiProjection.payload?.projection_complete === true
          || projectionState === "READY";
        if (!projectionComplete) {
          void record("UI_PROJECTION_INCOMPLETE", "SCREEN_PROJECTION", {
            projection_state: projectionState,
            status: uiProjection.status ?? "UNKNOWN",
            stale: uiProjection.stale === true,
            last_error: uiProjection.last_error ?? null,
            contract: RUNYI_UI_PROJECTION_CONTRACT,
          });
        } else {
          void resolve("UI_PROJECTION_INCOMPLETE", "SCREEN_PROJECTION");
        }
      }

      const documentOverflow = document.documentElement.scrollWidth > window.innerWidth + 1;
      if (documentOverflow) {
        void record("HORIZONTAL_OVERFLOW", "DOCUMENT", {
          scroll_width: document.documentElement.scrollWidth,
          viewport_width: window.innerWidth,
        });
      } else {
        void resolve("HORIZONTAL_OVERFLOW", "DOCUMENT");
      }

      document.querySelectorAll<HTMLElement>("[data-runyi-responsive-contract], [data-runyi-ui-tabs]").forEach((element, index) => {
        const elementKey = element.dataset.runyiUiElementKey || element.dataset.runyiResponsiveKind || ("ELEMENT_" + index);
        const overflow = element.scrollWidth > element.clientWidth + 1;
        if (overflow) {
          void record("COMPONENT_OVERFLOW", elementKey, {
            scroll_width: element.scrollWidth,
            client_width: element.clientWidth,
          });
        } else {
          void resolve("COMPONENT_OVERFLOW", elementKey);
        }
      });

      document.querySelectorAll<HTMLElement>("[data-runyi-ui-tabs]").forEach((element, index) => {
        const active = element.dataset.runyiUiActiveSection || "";
        const elementKey = element.dataset.runyiUiElementKey || ("TABS_" + index);
        const registered = active
          ? Boolean(element.querySelector('[data-runyi-ui-section="' + CSS.escape(active) + '"]'))
          : true;
        if (!registered) {
          void record("INVALID_ACTIVE_SECTION", elementKey, { active_section: active });
        } else {
          void resolve("INVALID_ACTIVE_SECTION", elementKey);
        }
      });
    };

    const schedule = () => {
      window.clearTimeout(validationTimer);
      validationTimer = window.setTimeout(validate, 90);
    };

    const mutationObserver = typeof MutationObserver !== "undefined"
      ? new MutationObserver(schedule)
      : null;

    mutationObserver?.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    schedule();

    return () => {
      mutationObserver?.disconnect();
      window.clearTimeout(validationTimer);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
    };
  }, [supabase, connected, screenKey, uiProjection]);

  return null;
}

export function RunyiUiCoordinator({
  contract,
  header,
  children,
}: {
  contract: RunyiUiLayoutContract;
  header?: ReactNode;
  children: ReactNode;
}) {
  const constrained = contract.mode === "WORKSPACE" || contract.mode === "CHAT" || contract.mode === "OVERLAY";

  if (!constrained) {
    return (
      <div
        data-runyi-ui-coordinator={RUNYI_UI_COORDINATOR_CONTRACT}
        data-runyi-ui-mode={contract.mode}
        data-runyi-ui-scroll-owner={contract.scrollOwner}
        className="mx-auto w-full min-w-0 max-w-none space-y-3 overflow-x-hidden px-1 pt-2 sm:px-2"
        style={{ paddingBottom: "calc(var(--runyi-ui-bottom-nav-height, 0px) + 2rem)" }}
      >
        {header}
        {children}
      </div>
    );
  }

  return (
    <div
      data-runyi-ui-coordinator={RUNYI_UI_COORDINATOR_CONTRACT}
      data-runyi-ui-mode={contract.mode}
      data-runyi-ui-scroll-owner={contract.scrollOwner}
      className="relative flex min-h-0 w-full flex-col overflow-hidden"
      style={{
        height: "var(--runyi-ui-available-height, 100dvh)",
        maxHeight: "var(--runyi-ui-available-height, 100dvh)",
      }}
    >
      {header ? <div className="shrink-0">{header}</div> : null}
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

export function RunyiUiScrollRegion({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div data-runyi-ui-scroll-region="content" className={"min-h-0 flex-1 overflow-y-auto overscroll-contain " + className}>
      {children}
    </div>
  );
}

export function RunyiUiFixedRegion({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div data-runyi-ui-fixed-region="true" className={"shrink-0 " + className}>{children}</div>;
}


export const RUNYI_RESPONSIVE_COMPONENT_CONTRACT = "RUNYI_UNIVERSAL_RESPONSIVE_COMPONENT_V1";

export const RUNYI_RESPONSIVE_ACTION_BUTTON_CLASS =
  "min-h-9 h-auto min-w-0 w-full justify-start overflow-hidden rounded-xl px-3 py-2 text-[10px]";

export function RunyiResponsiveActionGrid({
  children,
  className = "",
  minItemWidth = 144,
}: {
  children: ReactNode;
  className?: string;
  minItemWidth?: number;
}) {
  const resolvedMinWidth = Math.max(120, Math.min(320, minItemWidth));

  return (
    <div
      data-runyi-responsive-contract={RUNYI_RESPONSIVE_COMPONENT_CONTRACT}
      data-runyi-responsive-kind="action-grid"
      className={"grid min-w-0 gap-1.5 " + className}
      style={{
        gridTemplateColumns:
          "repeat(auto-fit, minmax(min(100%, " + String(resolvedMinWidth) + "px), 1fr))",
      }}
    >
      {children}
    </div>
  );
}

export function RunyiResponsiveActionLabel({ children }: { children: ReactNode }) {
  return (
    <span
      data-runyi-responsive-kind="action-label"
      className="min-w-0 flex-1 whitespace-normal break-words text-left leading-tight"
    >
      {children}
    </span>
  );
}


export type RunyiUiRegistryEntry = {
  entry_key: string;
  screen_key?: string;
  section_key?: string;
  target_screen_key?: string;
  display_name: string;
  description?: string | null;
  icon_key?: string | null;
  renderer_key?: string | null;
  metadata?: Record<string, any>;
};

export type RunyiUiControlPlaneContract = {
  contract: string;
  screen_key: string;
  policy?: Record<string, any>;
  sections: RunyiUiRegistryEntry[];
  navigation_entries: RunyiUiRegistryEntry[];
  authority?: Record<string, string>;
};

export function useRunyiUiControlPlane({
  supabase,
  connected,
  screenKey,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  screenKey: string;
}) {
  const [contract, setContract] = useState<RunyiUiControlPlaneContract | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!connected || !supabase) {
      setContract(null);
      return;
    }

    const load = async () => {
      const { data, error } = await supabase.rpc("runyi_service_ui_control_plane_read", {
        p_screen_key: screenKey,
      });
      if (!cancelled && !error && data) {
        setContract(data as RunyiUiControlPlaneContract);
      }
    };

    const refresh = () => void load();
    void load();
    window.addEventListener("runyi:ui-control-plane:invalidate", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("runyi:ui-control-plane:invalidate", refresh);
    };
  }, [supabase, connected, screenKey]);

  return contract;
}

export function RunyiRegistryTabs({
  entries,
  fallbackEntries = [],
  value,
  onChange,
  counts = {},
  surface = "PRIMARY",
  elementKey = "REGISTRY_TABS",
}: {
  entries: RunyiUiRegistryEntry[];
  fallbackEntries?: RunyiUiRegistryEntry[];
  value: string;
  onChange: (value: string) => void;
  counts?: Record<string, number | undefined>;
  surface?: string;
  elementKey?: string;
}) {
  const source = entries.length > 0 ? entries : fallbackEntries;
  const visible = source.filter((entry) =>
    String(entry.metadata?.navigation_surface ?? "PRIMARY").toUpperCase() === surface.toUpperCase()
  );

  return (
    <div
      data-runyi-ui-tabs="registry"
      data-runyi-ui-element-key={elementKey}
      data-runyi-ui-active-section={value}
      className="grid min-w-0 gap-1 rounded-2xl border border-foreground/8 bg-card p-1"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 82px), 1fr))" }}
    >
      {visible.map((entry) => {
        const id = String(entry.section_key ?? entry.metadata?.route ?? "");
        const active = id === value;
        const count = counts[id];
        return (
          <button
            key={entry.entry_key}
            type="button"
            data-runyi-ui-section={id}
            aria-current={active ? "page" : undefined}
            onClick={() => onChange(id)}
            className={
              "flex min-w-0 items-center justify-center gap-1 rounded-xl px-2 py-2 text-xs transition " +
              (active ? "bg-foreground/10 font-semibold text-foreground" : "text-foreground/45 hover:bg-foreground/[0.04]")
            }
          >
            <span className="min-w-0 truncate">{entry.display_name}</span>
            {typeof count === "number" ? (
              <span className="shrink-0 rounded-full bg-foreground/[0.06] px-1.5 py-0.5 text-[9px] text-foreground/45">
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

